import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import styles from "./Navbar.module.css";
import bvLogo from "../../assets/bv.jpg";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FiMenu, FiX, FiArrowRight, FiChevronDown, 
  FiInfo, FiGrid, FiUsers, FiMail, FiHome, 
  FiBox, FiBookOpen, FiStar, FiBriefcase, FiUserCheck,
  FiLogIn, FiUser, FiBarChart2, FiShoppingBag, FiHeart
} from "react-icons/fi";
import { supabase } from '../../utils/supabaseClient';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    { id: "home", label: "Home", icon: FiHome },
    { id: "studiox", label: "StudioX", icon: FiBox },
    { id: "blog", label: "Blog", icon: FiBookOpen },
  ];

  const companyItems = [
    { id: "about", label: "About", icon: FiInfo },
    { id: "services", label: "Services", icon: FiGrid },
    { id: "testimonials", label: "Testimonials", icon: FiStar },
    { id: "contact", label: "Contact", icon: FiMail },
  ];

  // Dynamic bottom navigation items for mobile - Changes based on admin status
  const getBottomNavItems = () => {
    const items = [
      { id: "home", label: "Home", icon: FiHome },
      { id: "services", label: "Services", icon: FiGrid },
      { id: "studiox", label: "StudioX", icon: FiBox },
      
    ];
    
    // If admin is logged in, show Dashboard instead of About
    if (isAdmin) {
      items.push({ id: "dashboard", label: "Dashboard", icon: FiBarChart2 });
    } else {
      items.push({ id: "about", label: "About", icon: FiInfo });
    }
    
    // Add profile/account at the end
    items.push({ 
      id: "profile", 
      label: user ? "Account" : "Login", 
      icon: FiUser 
    });
    
    return items;
  };

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setUser(session.user);
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single();
        setIsAdmin(profile?.is_admin === true);
      }
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        supabase
          .from('user_profiles')
          .select('is_admin')
          .eq('id', session.user.id)
          .single()
          .then(({ data }) => setIsAdmin(data?.is_admin === true));
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      if (location.pathname === "/") {
        const sections = [...navItems.map(item => item.id), ...companyItems.map(item => item.id)];
        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const rect = element.getBoundingClientRect();
            if (rect.top <= 150 && rect.bottom >= 150) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(`.${styles.dropdown}`)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [dropdownOpen]);

  const isHomePage = () => location.pathname === "/";
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (dropdownOpen) setDropdownOpen(false);
  };
  const closeMenu = () => setMenuOpen(false);

  const scrollToSection = (sectionId) => {
    closeMenu();
    setDropdownOpen(false);
    if (isHomePage()) {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(sectionId);
      }
    } else {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setActiveSection(sectionId);
        }
      }, 500);
    }
  };

  const handleGetStartedClick = () => {
    closeMenu();
    navigate("/contact");
  };

  const handleLogoClick = () => {
    closeMenu();
    if (isHomePage()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setActiveSection("home");
    } else {
      navigate("/");
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        setActiveSection("home");
      }, 100);
    }
  };

  const handleLoginClick = () => {
    closeMenu();
    navigate("/login");
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      setIsAdmin(false);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  const handleProfileClick = () => {
    closeMenu();
    navigate("/profile");
  };

  const handleAdminDashboardClick = () => {
    closeMenu();
    navigate("/admin");
  };

  const handleBottomNavClick = (itemId) => {
    if (itemId === 'profile') {
      if (user) {
        navigate('/profile');
      } else {
        navigate('/login');
      }
    } else if (itemId === 'dashboard') {
      navigate('/admin');
    } else if (itemId === 'services' || itemId === 'about') {
      scrollToSection(itemId);
    } else {
      scrollToSection(itemId);
    }
  };

  const bottomNavItems = getBottomNavItems();

  return (
    <>
      <motion.nav 
        className={`${styles.navbar} ${scrolled ? styles.scrolled : ""}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className={styles.navContainer}>
          <motion.div 
            className={styles.logo}
            onClick={handleLogoClick}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <img src={bvLogo} alt="BrandVerse Logo" className={styles.logoImage} />
          </motion.div>

          {/* Desktop Navigation */}
          <div className={styles.desktopNav}>
            <ul className={styles.desktopLinks}>
              {navItems.map((item) => (
                <li key={item.id} className={styles.navItem}>
                  <button 
                    onClick={() => scrollToSection(item.id)}
                    className={`${styles.navButton} ${activeSection === item.id ? styles.active : ""}`}
                  >
                    <item.icon className={styles.navIcon} />
                    <span>{item.label}</span>
                  </button>
                </li>
              ))}
              
              <li className={styles.dropdown}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={styles.dropdownButton}
                >
                  <FiBriefcase className={styles.navIcon} />
                  <span>Company</span>
                  <FiChevronDown className={`${styles.dropdownIcon} ${dropdownOpen ? styles.rotated : ""}`} />
                </button>
                
                {dropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    {companyItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={styles.dropdownItem}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </li>
            </ul>

            <div className={styles.rightButtons}>
              <button onClick={handleGetStartedClick} className={styles.ctaButton}>
                Get Started <FiArrowRight />
              </button>

              {isAdmin && (
                <button onClick={handleAdminDashboardClick} className={styles.adminButton}>
                  <FiBarChart2 /> Dashboard
                </button>
              )}

              {user ? (
                <div className={styles.userDropdown}>
                  <button className={styles.userButton}>
                    <FiUser /> {user.email?.split('@')[0]} <FiChevronDown />
                  </button>
                  <div className={styles.userDropdownMenu}>
                    <button onClick={handleProfileClick}>Profile</button>
                    <button onClick={handleLogout}>Logout</button>
                  </div>
                </div>
              ) : (
                <button onClick={handleLoginClick} className={styles.loginButton}>
                  <FiLogIn /> Login
                </button>
              )}
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <button className={styles.menuToggle} onClick={toggleMenu}>
            {menuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Bottom Navigation Bar - Dynamic (Dashboard replaces About for Admin) */}
      <div className={styles.bottomNavBar}>
        {bottomNavItems.map((item) => {
          const isActive = 
            (activeSection === item.id) || 
            (item.id === 'profile' && user && location.pathname === '/profile') ||
            (item.id === 'profile' && !user && location.pathname === '/login') ||
            (item.id === 'dashboard' && location.pathname === '/admin');
          
          return (
            <button
              key={item.id}
              className={`${styles.bottomNavItem} ${isActive ? styles.active : ''}`}
              onClick={() => handleBottomNavClick(item.id)}
            >
              <item.icon className={styles.bottomNavIcon} />
              <span className={styles.bottomNavLabel}>{item.label}</span>
              {isActive && <motion.span className={styles.bottomNavIndicator} layoutId="bottomIndicator" />}
            </button>
          );
        })}
      </div>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <div className={styles.mobileOverlay} onClick={closeMenu} />
            <motion.div 
              className={styles.mobileMenu}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25 }}
            >
              <div className={styles.mobileHeader}>
                <img src={bvLogo} alt="Logo" className={styles.mobileLogoImage} />
                <button onClick={closeMenu} className={styles.mobileCloseBtn}><FiX /></button>
              </div>
              <div className={styles.mobileLinksContainer}>
                <ul className={styles.mobileLinks}>
                  <li><div className={styles.mobileDivider}>Menu</div></li>
                  {navItems.map((item) => (
                    <li key={item.id}>
                      <button onClick={() => scrollToSection(item.id)} className={styles.mobileNavButton}>
                        <item.icon /> {item.label}
                      </button>
                    </li>
                  ))}
                  <li><div className={styles.mobileDivider}>Company</div></li>
                  {companyItems.map((item) => (
                    <li key={item.id}>
                      <button onClick={() => scrollToSection(item.id)} className={styles.mobileNavButton}>
                        <item.icon /> {item.label}
                      </button>
                    </li>
                  ))}
                  {isAdmin && (
                    <>
                      <li><div className={styles.mobileDivider}>Admin</div></li>
                      <li>
                        <button onClick={handleAdminDashboardClick} className={styles.mobileNavButton}>
                          <FiBarChart2 /> Dashboard
                        </button>
                      </li>
                    </>
                  )}
                </ul>
                <div className={styles.mobileFooter}>
                  <button onClick={handleGetStartedClick} className={styles.mobileCtaButton}>
                    Get Started <FiArrowRight />
                  </button>
                  {user && (
                    <button onClick={handleLogout} className={styles.mobileLogoutButton}>
                      Logout
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;